import { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from 'react';

import {
  Image02Icon,
  Notification01Icon,
  PaintBoardIcon,
  UserAccountIcon,
  UserAdd01Icon,
} from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import { FieldTypes } from 'constants/field-types-enum';
import { componentMap, getComponentProps } from 'helpers/question-config';
import patternValidators, { normalizeEmailList } from 'helpers/validators/pattern-validators';
import { useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import QuestionWrapper from 'components/Form/Questions/QuestionWrapper';
import { Input, InputGroup, Select, ToggleSwitchInput } from 'components/FormComponents';
import { TitleGroup } from 'components/TitleGroup/TitleGroup';

import { CommunicationHistoryList } from './CommunicationHistoryList';

export const CandidateInformation = forwardRef(
  (
    {
      candidate,
      company,
      brand,
      teams,
      companyUsers,
      companyChecks = [],
      referees = [],
      billingEntityOptionSet = null,
      onUpdate = () => {},
      hideBrand = false,
    },
    ref,
  ) => {
    const { t } = useTranslation();
    const {
      register,
      reset,
      control,
      handleSubmit,
      clearErrors,
      setValue,
      getValues,
      formState: { errors },
    } = useForm({
      defaultValues: {
        ...candidate,
        contact_number: candidate?.contact_number ?? '',
        ...(hideBrand ? { brand_name: brand?.name ?? '' } : {}),
      },
    });

    const user_id = useWatch({ control, name: 'user_id' });

    const [ccNotificationEmailsEnabled, setCcNotificationEmailsEnabled] = useState(
      !!candidate.cc_notification_emails,
    );

    const detailQuestions = useMemo(
      () => ({
        firstName: {
          id: 'first_name',
          code: 'first_name',
          type: FieldTypes.ShortText,
          text: t('common.firstName'),
          placeholder: t('check.candidateInformation.firstNamePlaceholder'),
          required: true,
        },
        lastName: {
          id: 'last_name',
          code: 'last_name',
          type: FieldTypes.ShortText,
          text: t('common.lastName'),
          placeholder: t('check.candidateInformation.lastNamePlaceholder'),
          required: true,
        },
        email: {
          id: 'email',
          code: 'email',
          type: FieldTypes.Email,
          text: t('common.email'),
          placeholder: t('check.candidateInformation.emailPlaceholder'),
          required: true,
        },
        contactNumber: {
          id: 'contact_number',
          code: 'contact_number',
          type: FieldTypes.PhoneNumber,
          text: t('common.phoneNumber'),
        },
        jobRole: {
          id: 'job_role',
          code: 'job_role',
          type: FieldTypes.ShortText,
          text: t('common.jobRole'),
          placeholder: t('check.candidateInformation.jobTitlePlaceholder'),
        },
        department: {
          id: 'department',
          code: 'department',
          type: FieldTypes.ShortText,
          text: t('common.department'),
          placeholder: t('check.candidateInformation.departmentPlaceholder'),
        },
        referenceCode: {
          id: 'reference_code',
          code: 'reference_code',
          type: FieldTypes.ShortText,
          text: t('check.infoFields.referenceCode'),
          placeholder: t('check.candidateInformation.referenceCodePlaceholder'),
        },
        brand: {
          id: 'brand_name',
          code: 'brand_name',
          type: FieldTypes.ReadOnly,
          text: 'Brand',
          defaultValue: brand?.name ?? '',
        },
        team: {
          id: 'team_id',
          code: 'team_id',
          type: FieldTypes.Select,
          text: t('common.team'),
          placeholder: t('common.team'),
          options: teams.map(team => ({ value: team.id, name: team.name })),
          useDefault: true,
        },
        billingEntity: billingEntityOptionSet?.options?.length
          ? {
              id: 'billing_entity_option_id',
              code: 'billing_entity_option_id',
              type: FieldTypes.Select,
              text: billingEntityOptionSet.name,
              placeholder: `Select ${billingEntityOptionSet.name}...`,
              options: billingEntityOptionSet.options.map(option => ({
                value: option.id,
                name: option.name,
              })),
              useSearch: true,
            }
          : null,
        document: {
          id: 'document_name',
          code: 'document_name',
          type: FieldTypes.ReadOnly,
          text: t('common.document'),
          placeholder: t('check.candidateInformation.documentPlaceholder'),
        },
      }),
      [t, teams, brand?.name, billingEntityOptionSet],
    );

    const renderDetailQuestion = question => (
      <QuestionWrapper
        key={question.code}
        question={question}
        questionIndex={0}
        fieldName={question.code}
        control={control}
        register={register}
        setValue={setValue}
        getValues={getValues}
        componentMap={componentMap}
        errors={errors}
        error={errors?.[question.code]}
        hideOptionalTag
        requirementVariantStyle="text"
        titleSize="small"
        gap="small"
        noMargin
        getComponentProps={value => {
          const props = getComponentProps({
            question,
            value,
            notForForm: true,
            noMargin: true,
          });
          if (question.type === FieldTypes.PhoneNumber) {
            return { ...props, defaultCountry: company?.country };
          }
          return props;
        }}
      />
    );

    const handleUpdate = async data => {
      const { brand_name: _brandName, ...updateData } = data;
      return onUpdate({
        ...updateData,
        contact_number: updateData.contact_number?.trim() || null,
        cc_notification_emails: ccNotificationEmailsEnabled
          ? normalizeEmailList(updateData?.cc_notification_emails)
          : '',
      });
    };

    useImperativeHandle(
      ref,
      () => ({
        submit: () => handleSubmit(handleUpdate)(),
      }),
      [handleSubmit, handleUpdate, ccNotificationEmailsEnabled],
    );

    useEffect(() => {
      if (!candidate) return;
      reset({
        ...candidate,
        contact_number: candidate.contact_number ?? '',
        ...(hideBrand ? { brand_name: brand?.name ?? '' } : {}),
      });
    }, [candidate, hideBrand, brand?.name]);

    useEffect(() => {
      setCcNotificationEmailsEnabled(!!candidate.cc_notification_emails);
    }, [candidate?.cc_notification_emails]);

    return (
      <form
        className="d-flex flex-column u-width-100 u-gap-medium p-8"
        onSubmit={handleSubmit(handleUpdate)}>
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-8">
          <div className="border border-neutral-4 rounded-3xl shadow-sm p-8 bg-neutral-1 flex flex-col gap-8">
            <TitleGroup
              icon={UserAccountIcon}
              title="Candidate details"
              subTitle="Personal and role information"
            />
            <div className="d-flex flex-column u-gap-medium">
              <div className="inline">
                {renderDetailQuestion(detailQuestions.firstName)}
                {renderDetailQuestion(detailQuestions.lastName)}
              </div>
              {renderDetailQuestion(detailQuestions.email)}
              {renderDetailQuestion(detailQuestions.contactNumber)}
              <div className="inline">
                {renderDetailQuestion(detailQuestions.jobRole)}
                {renderDetailQuestion(detailQuestions.department)}
              </div>
              {renderDetailQuestion(detailQuestions.referenceCode)}
              {hideBrand && renderDetailQuestion(detailQuestions.brand)}
              {renderDetailQuestion(detailQuestions.team)}
              {detailQuestions.billingEntity && renderDetailQuestion(detailQuestions.billingEntity)}
              {renderDetailQuestion(detailQuestions.document)}
            </div>
          </div>
          <div className="flex flex-col gap-8">
            <div className="border border-neutral-4 rounded-3xl shadow-sm p-8 bg-neutral-1 flex flex-col gap-8">
              <TitleGroup
                icon={UserAdd01Icon}
                title={t('check.candidateInformation.userAssignment')}
                subTitle={t('check.candidateInformation.assignedUserDescription')}
              />
              <Select
                name="user_id"
                placeholder={t('common.user')}
                register={register}
                value={user_id}
                useDefault
                useSearch>
                {companyUsers?.map(user => (
                  <Select.Item key={user.id} value={user.id}>
                    {user.first_name} {user.last_name} • {user.email}
                  </Select.Item>
                ))}
              </Select>
            </div>

            <ToggleSwitchInput
              noMaxWidth
              text={t('check.candidateInformation.notificationList')}
              subtext={t('check.candidateInformation.notificationListSubtext')}
              onChange={e => {
                setCcNotificationEmailsEnabled(e.target.checked);
                if (!e.target.checked) {
                  clearErrors('cc_notification_emails');
                }
              }}
              icon={Notification01Icon}
              className="u-margin-bottom--0"
              value={ccNotificationEmailsEnabled}>
              <InputGroup title={t('check.candidateInformation.emails')} noMargin>
                <div className="d-flex flex-column u-width-100 u-gap-small">
                  <Input
                    name="cc_notification_emails"
                    placeholder={t('check.candidateInformation.ccEmailsPlaceholder')}
                    register={register}
                    validators={{
                      setValueAs: normalizeEmailList,
                      validate: value => {
                        if (!ccNotificationEmailsEnabled) return true;
                        if (!value?.trim()) {
                          return t('check.candidateInformation.ccEmailsRequiredError');
                        }
                        return patternValidators.emailList.validate(value);
                      },
                    }}
                    error={errors?.cc_notification_emails?.message}
                    disabled={!ccNotificationEmailsEnabled}
                  />
                  <span className="text-text-tertiary text-[12px] font-light">
                    {t('check.candidateInformation.ccEmailsHint')}
                  </span>
                </div>
              </InputGroup>
            </ToggleSwitchInput>
            {!hideBrand && (
              <div className="bg-neutral-1 p-8 rounded-3xl border border-border-primary flex flex-col gap-8 shadow-sm">
                <div className="flex justify-between gap-8 items-start">
                  <TitleGroup
                    icon={PaintBoardIcon}
                    title="Branding"
                    subTitle="Applied to emails and candidate-facing pages"
                  />
                  {brand?.id && (
                    <Link
                      to={`/branding_details/${brand.id}?tab=general`}
                      target="_blank"
                      className="text-primary-4 text-lg font-medium shrink-0 whitespace-nowrap">
                      View brand
                    </Link>
                  )}
                </div>
                <div className="p-8 border border-border-primary  rounded-3xl bg-neutral-2 ">
                  {brand?.brand_logo ? (
                    <div
                      className="bg-contain bg-center bg-no-repeat min-h-48"
                      style={
                        brand?.brand_logo
                          ? { backgroundImage: `url(${brand.brand_logo})` }
                          : undefined
                      }
                      role="img"
                      aria-label={t('check.candidateInformation.brandLogoAriaLabel')}
                    />
                  ) : (
                    <div className="flex flex-col items-center justify-center gap-2">
                      <HugeiconsIcon
                        icon={Image02Icon}
                        size="40"
                        strokeWidth={1.5}
                        className="text-neutral-6"
                      />
                      <span className="text-neutral-6 text-2xl font-regular">
                        {t('check.candidateInformation.noLogoConfigured')}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
          <CommunicationHistoryList
            candidate={candidate}
            company={company}
            brand={brand}
            companyChecks={companyChecks}
            referees={referees}
          />
        </div>
      </form>
    );
  },
);
CandidateInformation.displayName = 'CandidateInformation';
